import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

// ✅ Define User Type
export interface User {
  id: number;
  email: string;
  username: string;
  slug: string;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  avatar_url: string | null;
  roles: string[];
  organizer_ids: number[];
}

// ✅ Context Type
interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// ✅ Declare UserContext ONLY ONCE
export const UserContext = createContext<UserContextType | null>(null);

const getCSRFToken = (): string => {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) return metaTag.getAttribute('content') || '';

  const name = 'XSRF-TOKEN=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (let cookie of cookieArray) {
    cookie = cookie.trim();
    if (cookie.startsWith(name)) return cookie.substring(name.length);
  }

  console.warn('⚠️ No CSRF token found in meta tag or cookie');
  return '';
};

// ✅ Provider Component
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // ✅ Add a loading state

  // ✅ Function to fetch user data
  const fetchUser = async () => {
    try {
      const csrfToken = getCSRFToken();
      const response = await fetch('/api/v1/users/current', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
      });

      if (response.status === 401) {
        setUser(null);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch user data.');

      const data = await response.json();

      setUser(data.user || null);
    } catch (error) {
      console.error('⚠️ Fetch Error:', error);
      setUser(null);
    } finally {
      setIsLoading(false); // ✅ Ensure loading state is updated
    }
  };

  // ✅ Logout function - Ensures session is properly cleared
  const logout = async () => {
    try {
      const csrfToken = getCSRFToken();
      await fetch('/logout', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
      });
    } catch (error) {
      console.error('⚠️ Logout error:', error);
    } finally {
      setUser(null);
      window.location.href = '/'; // ✅ Redirect to home page after logout
    }
  };

  // ✅ Fetch user on mount
  useEffect(() => {
    fetchUser();
  }, []);

  // ✅ Prevent children from rendering until user data is loaded
  if (isLoading) {
    return <p></p>;
  }

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser: fetchUser, logout }}>{children}</UserContext.Provider>
  );
};

// ✅ Custom Hook for Consuming User Context
export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('❌ useUser must be used within a <UserProvider>');
  }

  return context;
};
